import React from "react";
import { BannerProps } from "@/types/banner";
import { defaultBannerContent } from "./defaultBannerContent";
import { useBannerRotation } from "./useBannerRotation";
import { LinkWrapper } from "@/components/LinkWrapper";
import { cn } from "@/lib/utils";

const BigBoldBanner: React.FC<BannerProps> = ({
  items = defaultBannerContent,
  rotationInterval = 5000,
  ...forwardedProps
}) => {
  const { currentIndex, isTransitioning, pause, resume } = useBannerRotation({
    itemCount: items.length,
    interval: rotationInterval,
  });

  if (items.length === 0) {
    return null;
  }

  const currentItem = items[currentIndex];
  const hasMultipleItems = items.length > 1;

  return (
    <div
      role="region"
      aria-label="Announcements"
      className="relative w-full overflow-hidden bg-primary py-3 sm:py-2.5"
      onMouseEnter={pause}
      onMouseLeave={resume}
      onFocus={pause}
      onBlur={resume}
      {...forwardedProps}
    >
      <div className="container mx-auto px-4">
        {/* Fixed height container to prevent layout shift during rotation */}
        <div
          className="flex h-[52px] items-center justify-center sm:h-[28px]"
          style={{ perspective: "1000px" }}
        >
          <div
            aria-live="polite"
            aria-atomic="true"
            className={cn(
              "flex flex-col items-center justify-center gap-1.5 text-center transition-transform duration-300 sm:flex-row sm:gap-2",
              isTransitioning && "animate-flip-out"
            )}
            style={{
              transformStyle: "preserve-3d",
              backfaceVisibility: "hidden",
            }}
          >
            <span className="text-xs font-bold uppercase leading-tight tracking-wider text-primary-foreground sm:text-sm">
              {currentItem.text}
              {/* Screen reader context for rotating content */}
              {hasMultipleItems && (
                <span className="sr-only">
                  {` (Announcement ${currentIndex + 1} of ${items.length})`}
                </span>
              )}
            </span>
            {currentItem.linkText && currentItem.linkUrl && (
              <LinkWrapper
                to={currentItem.linkUrl}
                className="inline-flex shrink-0 items-center gap-1 text-xs font-bold uppercase tracking-wider text-primary-foreground underline underline-offset-2 transition-opacity hover:opacity-80 focus:outline-none focus:ring-2 focus:ring-primary-foreground/50 focus:ring-offset-2 focus:ring-offset-primary sm:text-sm"
              >
                {currentItem.linkText}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-3.5 w-3.5 sm:h-4 sm:w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={2}
                  aria-hidden="true"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
              </LinkWrapper>
            )}
          </div>
        </div>
      </div>

      {/* CSS for flip animation */}
      <style>{`
        @keyframes flipOut {
          0% {
            transform: rotateX(0deg);
            opacity: 1;
          }
          50% {
            transform: rotateX(-90deg);
            opacity: 0;
          }
          51% {
            transform: rotateX(90deg);
            opacity: 0;
          }
          100% {
            transform: rotateX(0deg);
            opacity: 1;
          }
        }
        .animate-flip-out {
          animation: flipOut 0.6s ease-in-out;
        }
      `}</style>
    </div>
  );
};

export default BigBoldBanner;
